/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.root = this.closest('site-header');
      this.dialog = this.querySelector('[data-predictive-search-dialog]');
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.closeButtons = [...this.querySelectorAll('[data-search-close]')];
      this.mobileAnnouncement = this.querySelector('[data-search-mobile-announcement]');
      this.initialMarkup = this.results?.innerHTML || '';
      this.cache = new Map();

      const selector = `[data-search-open="${this.dialog?.id}"]`;
      this.openButtons = [...(this.root?.querySelectorAll(selector) || [])];
      this.openButtons.forEach((button) => button.addEventListener('click', () => this.open(button), { signal }));
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(), { signal }));
      this.clearButton?.addEventListener('click', () => this.clear(), { signal });
      this.input?.addEventListener('input', () => this.onInput(), { signal });
      this.input?.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.results?.addEventListener('keydown', (event) => this.onResultsKeydown(event), { signal });
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.requestController?.abort();
      window.clearTimeout(this.debounceTimer);
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.trigger = trigger;
      this.syncAnnouncement();
      document.querySelectorAll('mobile-menu').forEach((menu) => {
        if (typeof menu.close === 'function') menu.close({ restoreFocus: false, immediate: true });
      });
      this.dialog.showModal();
      this.input?.setAttribute('aria-expanded', 'false');
      document.dispatchEvent(new CustomEvent('theme:dialog-opened'));
      window.requestAnimationFrame(() => this.input?.focus({ preventScroll: true }));
    }

    syncAnnouncement() {
      if (!this.mobileAnnouncement) return;
      const source = document.querySelector('[data-announcement-bar]');
      this.mobileAnnouncement.replaceChildren();
      if (!source) return;
      const clone = source.cloneNode(true);
      clone.removeAttribute('data-announcement-bar');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-label');
      this.mobileAnnouncement.append(clone);
    }

    close() {
      if (!this.dialog?.open) return;
      this.requestController?.abort();
      this.dialog.close();
      this.input?.setAttribute('aria-expanded', 'false');
      document.dispatchEvent(new CustomEvent('theme:dialog-closed'));
      const triggerIsHiddenInDialog = this.trigger?.closest('dialog') && !this.trigger.closest('dialog').open;
      const returnTarget = triggerIsHiddenInDialog
        ? this.root?.querySelector('[data-mobile-menu-open]')
        : this.trigger;
      returnTarget?.focus({ preventScroll: true });
    }

    clear() {
      if (!this.input || !this.results) return;
      this.input.value = '';
      this.results.innerHTML = this.initialMarkup;
      this.results.classList.remove('is-loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.setStatus('');
      this.input.focus();
    }

    onInput() {
      window.clearTimeout(this.debounceTimer);
      const term = this.input?.value.trim() || '';
      if (!term) {
        this.clear();
        return;
      }
      this.debounceTimer = window.setTimeout(() => this.search(term), 200);
    }

    async search(term) {
      if (!this.results || !this.input) return;
      const key = term.toLocaleLowerCase();
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      this.requestController?.abort();
      this.requestController = new AbortController();
      this.results.classList.add('is-loading');
      this.results.innerHTML = '';
      this.setStatus('Loading');

      const root = window.Shopify?.routes?.root || '/';
      const parameters = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
        section_id: this.dataset.resultsSectionId || 'predictive-search'
      });

      try {
        const response = await fetch(`${root}search/suggest?${parameters}`, {
          signal: this.requestController.signal,
          headers: { Accept: 'text/html' }
        });
        const text = await response.text();
        if (!response.ok) throw new Error(`${response.status}: ${text}`);
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        const markup = section?.innerHTML || parsed.body.innerHTML;
        this.cache.set(key, markup);
        this.render(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        this.results.classList.remove('is-loading');
        this.setStatus('Search is temporarily unavailable.');
        document.dispatchEvent(new CustomEvent('theme:toast', {
          detail: { message: 'Search is temporarily unavailable. Please try again.', type: 'error' }
        }));
      }
    }

    render(markup) {
      if (!this.results || !this.input) return;
      this.results.classList.remove('is-loading');
      this.results.innerHTML = markup;
      const options = [...this.results.querySelectorAll('[role="option"] a')];
      options.forEach((option) => option.setAttribute('tabindex', '0'));
      this.input.setAttribute('aria-expanded', options.length > 0 ? 'true' : 'false');
      const summary = this.results.querySelector('.predictive-search-rendered__summary, .predictive-search-rendered__empty');
      this.setStatus(summary?.textContent.trim() || 'Search results updated.');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const results = [...(this.results?.querySelectorAll('[role="option"] a') || [])];
      const nextResult = event.key === 'ArrowDown' ? results[0] : results.at(-1);
      if (!nextResult) return;
      event.preventDefault();
      nextResult.focus();
    }

    onResultsKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const options = [...this.results.querySelectorAll('[role="option"] a')];
      const active = event.target.closest('[role="option"] a');
      const index = options.indexOf(active);
      if (index < 0) return;
      event.preventDefault();
      if (event.key === 'Home') options[0]?.focus();
      else if (event.key === 'End') options.at(-1)?.focus();
      else if (event.key === 'ArrowDown') (options[index + 1] || this.input)?.focus();
      else (options[index - 1] || this.input)?.focus();
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
